import React, { useEffect, useImperativeHandle, useState, forwardRef } from "react";
import { Table, theme } from "antd";

import type { ScheduleDistribution } from "@api/types/transactions/types.gen";
import type { BeneficiaryItem, Investment , Frequency } from "../types";

import { deriveBeneficiaryInitFromDistributions } from "./helpers/deriveBeneficiaryInitFromDistributions";
import useAmountByKey from "./helpers/useAmountByKey";
import useColumns from "./helpers/useColumns";
import useItems from "./helpers/useItems";
import BeneficiariesTableSummary from "./BeneficiariesTableSummary";

type Props = {
  investments: Investment[];
  shouldRoundCents: boolean;
  distributionFrequency: Frequency;
  initialDistributions?: ScheduleDistribution[];
}

type SelectedDistribution = Omit<ScheduleDistribution, 'transactionType'>;

export type BeneficiariesTableHandle = {
  getSelectedDistributions: () => SelectedDistribution[];
  getTotalAmount: () => number;
}

const BeneficiariesTable = forwardRef<BeneficiariesTableHandle, Props>(({
  initialDistributions = undefined,
  investments,
  shouldRoundCents,
  distributionFrequency,
}, ref) => {
  const { token } = theme.useToken();

  const [ selectedRowKeys, setSelectedRowKeys ] = useState<React.Key[]>([]);

  const items = useItems(investments);
  const { amountByKey, setCustomAmountByKey } = useAmountByKey(items, distributionFrequency, shouldRoundCents);

  const columns = useColumns(
    amountByKey,
    setCustomAmountByKey,
    selectedRowKeys,
    distributionFrequency,
    shouldRoundCents,
  );

  useImperativeHandle(ref, () => ({
    getSelectedDistributions: () =>
      items
        .filter(({ key }) => selectedRowKeys.includes(key))
        .map(({ investmentId, investmentName, investorAccountId, tierId, returnRate, investedAmount, key }) => ({
          investmentId,
          investmentName,
          investorAccountId,
          tierId,
          returnRate,
          investedAmount,
          amount: amountByKey[key],
        })),
    getTotalAmount: () =>
      selectedRowKeys
        .map(key => amountByKey[key as string])
        .reduce((sum, value) => sum + (Number(value) || 0), 0),
  }), [ items, selectedRowKeys, amountByKey ]);

  // Create / no saved distributions: reset selection when the investment list changes only.
  // Do not depend on distributionFrequency or shouldRoundCents — defaults for non-custom rows come
  // from useAmountByKey; re-running here would clear user-entered custom amounts when those change.
  useEffect(() => {
    if (initialDistributions !== undefined) {
      return;
    }

    setSelectedRowKeys(items.map(({ key }) => key));
    setCustomAmountByKey({});
  }, [ items, initialDistributions, setCustomAmountByKey ]);

  // Edit: hydrate selection and which rows are truly custom from API data.
  // Omit distributionFrequency and shouldRoundCents from deps: derive compares amounts to defaults
  // for the current frequency/rounding; when the user changes those in the form, saved amounts must
  // not be re-compared against the new defaults (that would treat old defaults as custom and block
  // updating to the new default in useAmountByKey).
  useEffect(() => {
    if (initialDistributions === undefined) {
      return;
    }
        
    const { selectedKeys, customAmountByKey: custom } = deriveBeneficiaryInitFromDistributions(
      items,
      initialDistributions,
      distributionFrequency,
      shouldRoundCents,
    );

    setSelectedRowKeys(selectedKeys);
    setCustomAmountByKey(custom);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- distributionFrequency, shouldRoundCents: see comment above
  }, [ items, initialDistributions ]);

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const tableProps = {
    size: "small" as const,
    rowKey: "key",
    bordered: true,
    dataSource: items,
    pagination: false as const,
    columns,
    rowSelection,
    onRow: ({ key }: { key: string }) => ({
      style: selectedRowKeys.includes(key)
        ? undefined
        : {
          backgroundColor: token.colorFillAlter,
          color: token.colorTextDisabled,
        },
    }),
    summary: () => (
      <BeneficiariesTableSummary
        amountByKey={amountByKey}
        selectedRowKeys={selectedRowKeys as string[]}
      />
    ),
  };

  return (
    <Table<BeneficiaryItem> {...tableProps} />
  );
});

BeneficiariesTable.displayName = "BeneficiariesTable";

export default BeneficiariesTable;
